import React, { useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Box, Typography } from '@mui/material';
import LineChartComponent from '../LineChartComponent/LineChartComponent';

const DEFAULT_SELECTED_SERIES = ['return', 'volatility', 'ratio'];

const CHART_SERIES = {
  return: {
    color: 'rgba(33, 150, 243, 1)',
    yAxisID: 'yPercent',
  },
  volatility: {
    color: 'rgba(239, 83, 80, 1)',
    yAxisID: 'yPercent',
  },
  ratio: {
    color: 'rgba(212, 175, 55, 1)',
    backgroundColor: 'rgba(212, 175, 55, 0.2)',
    fill: 'origin',
    yAxisID: 'yRatio',
  },
};

const SERIES_LABELS = {
  Absolute: {
    return: 'Return',
    volatility: 'Volatility',
    ratio: 'Sharpe Ratio',
  },
  Active: {
    return: 'Return',
    volatility: 'Tracking Error',
    ratio: 'Information Ratio',
  },
};

const buildSeriesLookup = (payload) => {
  if (!payload?.labels || !Array.isArray(payload?.datasets)) {
    return new Map();
  }

  const values = Array.isArray(payload.datasets[0]?.data) ? payload.datasets[0].data : [];

  return new Map(
    payload.labels.map((label, index) => {
      const rawValue = values[index];
      const numericValue = Number(rawValue);
      return [label, Number.isFinite(numericValue) ? numericValue : null];
    }),
  );
};

const getRatioLabel = (selectedType) => SERIES_LABELS[selectedType].ratio;

const getPercentAxisTitle = (selectedType, selectedSeries) => {
  const hasReturn = selectedSeries.includes('return');
  const hasVolatility = selectedSeries.includes('volatility');

  if (hasReturn && hasVolatility) {
    return selectedType === 'Active' ? 'Return / Tracking Error (%)' : 'Return / Volatility (%)';
  }
  if (hasReturn) {
    return 'Return (%)';
  }
  if (hasVolatility) {
    return selectedType === 'Active' ? 'Tracking Error (%)' : 'Volatility (%)';
  }
  return '';
};

const getRatioAxisTitle = (selectedType) => getRatioLabel(selectedType);

const ChartSelection = ({ data }) => {
  const [selectedType, setSelectedType] = useState('Active');
  const [selectedPeriod, setSelectedPeriod] = useState(12);
  const [selectedSeries, setSelectedSeries] = useState(() => [...DEFAULT_SELECTED_SERIES]);

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setSelectedType(newType);
    }
  };

  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setSelectedPeriod(newPeriod);
    }
  };

  const handleSeriesChange = (event, newSeries) => {
    setSelectedSeries(Array.isArray(newSeries) ? newSeries : []);
  };

  const chartData = useMemo(() => {
    const rollingReturn = data?.[selectedType]?.[selectedPeriod]?.rolling_return;
    const rollingVolatility = data?.[selectedType]?.[selectedPeriod]?.rolling_volatility;
    const rollingRatio = data?.[selectedType]?.[selectedPeriod]?.rolling_ratio;
    const returnLookup = buildSeriesLookup(rollingReturn);
    const volatilityLookup = buildSeriesLookup(rollingVolatility);
    const backendRatioLookup = buildSeriesLookup(rollingRatio);
    const hasBackendRatio = Boolean(rollingRatio);
    const labels = Array.from(
      new Set([
        ...(Array.isArray(rollingReturn?.labels) ? rollingReturn.labels : []),
        ...(Array.isArray(rollingVolatility?.labels) ? rollingVolatility.labels : []),
        ...(Array.isArray(rollingRatio?.labels) ? rollingRatio.labels : []),
      ]),
    );

    if (!labels.length) {
      return { labels: [], datasets: [] };
    }

    const seriesLookups = {
      return: returnLookup,
      volatility: volatilityLookup,
      ratio: backendRatioLookup,
    };
    const hasPercentSeries = selectedSeries.includes('return') || selectedSeries.includes('volatility');
    const hasRatioSeries = selectedSeries.includes('ratio') && hasBackendRatio;

    const datasets = selectedSeries.map((seriesKey) => {
      const config = CHART_SERIES[seriesKey];
      const lookup = seriesLookups[seriesKey];
      if (!config || !lookup || (seriesKey === 'ratio' && !hasBackendRatio)) {
        return null;
      }

      const yAxisID = seriesKey === 'ratio' && !hasPercentSeries ? 'yPercent' : config.yAxisID;

      return {
        label: seriesKey === 'ratio' ? getRatioLabel(selectedType) : SERIES_LABELS[selectedType][seriesKey],
        data: labels.map((label) => lookup.get(label) ?? null),
        borderColor: config.color,
        backgroundColor: config.backgroundColor,
        borderDash: config.borderDash,
        fill: config.fill ?? false,
        yAxisID,
        valueMultiplier: yAxisID === 'yPercent' && seriesKey !== 'ratio' ? 100 : 1,
      };
    }).filter(Boolean);

    const scales = {
      x: {
        type: 'time',
        time: { unit: 'month' },
      },
      yPercent: {
        position: 'left',
        display: hasPercentSeries || hasRatioSeries,
        format: hasPercentSeries ? 'percent' : 'number',
        valueMultiplier: hasPercentSeries ? 100 : 1,
        alignZero: hasPercentSeries && hasRatioSeries,
        title: {
          display: Boolean(
            hasPercentSeries
              ? getPercentAxisTitle(selectedType, selectedSeries)
              : getRatioAxisTitle(selectedType),
          ),
          text: hasPercentSeries
            ? getPercentAxisTitle(selectedType, selectedSeries)
            : getRatioAxisTitle(selectedType),
        },
      },
    };

    if (hasRatioSeries && hasPercentSeries) {
      scales.yRatio = {
        position: 'right',
        display: true,
        format: 'number',
        alignZero: true,
        title: {
          display: true,
          text: getRatioAxisTitle(selectedType),
        },
        grid: {
          drawOnChartArea: false,
        },
      };
    }

    return {
      labels,
      datasets,
      scales,
    };
  }, [data, selectedPeriod, selectedSeries, selectedType]);

  const ratioLabel = useMemo(() => {
    return getRatioLabel(selectedType);
  }, [selectedType]);

  const ratioUnavailable = useMemo(
    () => selectedSeries.includes('ratio') && !data?.[selectedType]?.[selectedPeriod]?.rolling_ratio,
    [data, selectedPeriod, selectedSeries, selectedType],
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          },
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={handleTypeChange}
            aria-label="Chart Type"
            size="small"
          >
            <ToggleButton value="Absolute" aria-label="Absolute">
              Absolute
            </ToggleButton>
            <ToggleButton value="Active" aria-label="Active">
              Active
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={selectedSeries}
            onChange={handleSeriesChange}
            aria-label="Metric Selection"
            size="small"
            sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
          >
            <ToggleButton value="return" aria-label="Return" sx={{ whiteSpace: 'nowrap' }}>
              Return
            </ToggleButton>
            <ToggleButton
              value="volatility"
              aria-label={SERIES_LABELS[selectedType].volatility}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {SERIES_LABELS[selectedType].volatility}
            </ToggleButton>
            <ToggleButton
              value="ratio"
              aria-label={ratioLabel}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {ratioLabel}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <ToggleButtonGroup
            value={selectedPeriod}
            exclusive
            onChange={handlePeriodChange}
            aria-label="Time Period"
            size="small"
          >
            <ToggleButton value={12} aria-label="1 Year">
              1 yr
            </ToggleButton>
            <ToggleButton value={36} aria-label="3 Years">
              3 yr
            </ToggleButton>
            <ToggleButton value={60} aria-label="5 Years">
              5 yr
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box width="100%" mt={2}>
        {selectedSeries.length ? (
          <LineChartComponent chartData={chartData} />
        ) : (
          <Typography align="center" color="text.secondary">
            Select at least one metric to display the chart.
          </Typography>
        )}
        {ratioUnavailable && (
          <Typography align="center" color="text.secondary" sx={{ mt: 1 }}>
            Rerun this analysis to load the backend-calculated {ratioLabel}.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChartSelection;
